<template>
  <div :class="['advertiser-container', errors.api ? 'slide-down' : 'slide-up']">

    <div :class="['error-message-bubble', errors.api ? 'slide-down' : 'slide-up']"> {{errors.api}} </div>

    <div v-if="newUser">
      <h3>new user</h3>
    </div>
    <div v-else>
      <h3>edit user</h3>
      <h4 class="user-email">{{userEmail}}</h4>
    </div>

    <div>
      <loading-icon v-if="userLoading"/>
      <div v-else class="info-wrapper">
        <div class="input-wrapper email">
          <label v-if="newUser" for="email">email<sup>*</sup></label>
          <input
            v-if="newUser"
            id="email"
            name="email"
            type="email"
            @blur="validateEmail()"
            v-model="userEmail">
          <div v-if="errors.email" class="error-message">{{errors.email}}</div>
        </div>

        <div class="input-wrapper phone">
          <label for="phone_number">mobile number</label>
          <input
            id="phone_number"
            name="phone_number"
            type="tel"
            @blur="validatePhoneNumber()"
            v-model="phoneNumber">
          <div v-if="errors.phone" class="error-message">{{errors.phone}}</div>
        </div>

        <div class="input-wrapper name">
          <label for="name">name</label>
          <input
            id="name"
            name="name"
            type="text"
            v-model="name"
            maxlength="64">
        </div>

        <div class="checkboxes-wrapper">
          <div v-if="isBillingUser" class="add-user-permission checkbox-wrapper">
            <sb-checkbox class="checkbox" :checked.sync="all"/>
            add user to all associated accounts
          </div>
          <div class="inventory-permission checkbox-wrapper">
            <sb-checkbox class="checkbox" :checked.sync="inventoryPermission"/>
            receive inventory request emails
          </div>
          <div class="inventory-permission checkbox-wrapper">
            <sb-checkbox class="checkbox" :checked.sync="sponsorshipPermission"/>
            receive sponsorship request emails
          </div>
        </div>
        <button @click="patchAdvertiserUser" class="button done-button">done</button>
      </div>
    </div>
    <router-link class="cancel" to="/settings/manage-users">
      cancel
    </router-link>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { brandsAPI } from '../../services/api/brands'
import LoadingIcon from '../Shared/LoadingIcon'
import { validateEmailUtil, validatePhoneUtil } from "../../utilities"
import { advertiserUserAPI } from '../../services/api/advertiserUserAPI'

export default {
  name: 'AdvertiserUser',

  props: {
    currentAdvertiser: Object
  },

  components: {
    LoadingIcon
  },

  data() {
    return {
      advertiserUserId: null,
      all: false,
      isBillingUser: false,
      emailValid: true,
      errors: {},
      genericApiError: "error, please try again or contact support",
      name: "",
      newUser: true,
      phoneNumber: "",
      phoneValid: true,
      user: [],
      userEmail: null,
      userLoading: null,
      inventoryPermission: false,
      sponsorshipPermission: false
    }
  },

  created() {
    const urlPath = this.$route?.params?.id

    if (urlPath) {
      this.newUser = false
      this.advertiserUserId = JSON.parse(urlPath)
      this.getAdvertiserUser()
    }
  },

  mounted() {
    this.billingUser(this.currentAdvertiser.id, this.userId)
  },

  computed: {
    ...mapState(['userId'])
  },

  methods: {
    async billingUser(brandId, id) {
      this.userLoading = true
      try {
        const response = await brandsAPI.checkBillingUser(brandId, id)
        this.isBillingUser = response?.permissions
      } catch (error) {
        this.configureError({errorMessage: this.genericApiError})
      }
      this.userLoading = false
    },

    async createAdvertiserUser() {
      this.validateBeforeSubmission()
      if (this.emailValid && this.phoneValid) {
        this.userLoading = true
        try {
          const response = await advertiserUserAPI.createAdvertiserUser({
            advertiserID: this.currentAdvertiser.id,
            phoneNumber: this.phoneNumber,
            email: this.userEmail,
            name: this.name,
            belongsToAllSiblings: this.all,
            inventoryPermission: this.inventoryPermission,
            sponsorshipPermission: this.sponsorshipPermission
          })
          const error = response.errors
          if (error) this.configureError({errorMessage: error})
          else this.$router.push({ path: "/settings/manage-users" })
        } catch (error) {
          this.configureError({errorMessage: this.genericApiError})
        } finally { this.userLoading = false }
      }
    },

    async getAdvertiserUser() {
      this.userLoading = true
      try {
        const response = await advertiserUserAPI.getAdvertiserUser(this.currentAdvertiser.id, this.advertiserUserId)
        const advertiserUser = response?.users_advertiser
        const user = advertiserUser?.user
        this.all = advertiserUser?.belongs_to_all_siblings
        this.inventoryPermission = advertiserUser?.receive_inventory_request_email
        this.sponsorshipPermission = advertiserUser?.receive_sponsor_request_email
        this.userEmail = user.email
        this.phoneNumber = user.phone_number
        this.name = user.name
      } catch (error) {
        this.configureError({errorMessage: this.genericApiError})
      }
      this.userLoading = false
    },

    async updateUserApiRequest() {
      this.userLoading = true

      try {
        const response = await advertiserUserAPI.updateAdvertiserUser({
          advertiserID: this.currentAdvertiser.id,
          userID: this.advertiserUserId,
          phoneNumber: this.phoneNumber,
          name: this.name,
          belongsToAllSiblings: this.all,
          inventoryPermission: this.inventoryPermission,
          sponsorshipPermission: this.sponsorshipPermission
        })
        const error = response.errors
        if (error) this.configureError({errorMessage: response.errors})
        else this.$router.push("/settings/manage-users")
      } catch (error) {
        this.configureError({errorMessage: this.genericApiError})
      } finally { this.userLoading = false }
    },

    /**
     * @desc Check against passed in condition and manage displaying errors.
     *       If condition is true, add the key-value pair to this.errors
     *       If condition is false, remove the key-value pair from this.errors
     * @param {boolean} condition - boolean value to see if remove or add error
     * @param {string} errorKey - key for error
     * @param {string} errorMessage - value for error
     */
    configureError({condition = false, errorKey = "api", errorMessage}) {
      condition ? this.$delete(this.errors, errorKey) : this.$set(this.errors, errorKey, errorMessage)
    },

    patchAdvertiserUser() {
      this.newUser ? this.createAdvertiserUser() : this.updateAdvertiserUser()
    },

    updateAdvertiserUser() {
      this.validateBeforeSubmission()
      if (this.name?.length > 0 || this.phoneValid) this.updateUserApiRequest()
    },

    validateBeforeSubmission() {
      this.newUser && this.validateEmail()
      this.validatePhoneNumber()
    },

    validateEmail() {
      this.emailValid = validateEmailUtil(this.userEmail)
      const emailError = this.userEmail?.length > 0 ? "enter a valid email" : "field required"
      this.configureError({condition: this.emailValid, errorKey: "email", errorMessage: emailError})
    },

    validatePhoneNumber() {
      this.phoneValid = this.phoneNumber?.length > 0 ? validatePhoneUtil(this.phoneNumber) : true
      this.configureError({condition: this.phoneValid, errorKey: "phone", errorMessage: "enter a valid phone number"})
    }
  }
}

</script>

<style lang="sass" scoped>
@import "../../assets/sass/colors"

.advertiser-container
  padding-top: 2rem
  margin: 0 auto
  max-width: 700px
  transition: all 0.7s ease
  &.slide-up
    margin: auto
  &.slide-down
    margin: 3rem auto auto

  .checkbox-wrapper
    align-items: center
    display: flex
    margin-top: 15px

    .checkbox
      margin-right: 10px
      border-radius: 5px

  h3
    text-align: center

  .user-email
    font-size: x-large
    margin-top: 20px
    text-align: center

  .info-wrapper
    display: flex
    flex-direction: column
    margin: 1.5rem auto 0
    width: 300px

    .input-wrapper
      display: flex
      flex-direction: column
      margin-bottom: .5rem
    .input-wrapper.name
      margin-bottom: 2rem

    label
      margin: .5rem auto .25rem auto
      font-size: .9rem
      width: 100%

    input
      border-radius: 6px
      display: block
      font-size: 1rem
      margin: 0
      outline: none
      padding: .5rem

    input.error
      border: 1px solid red

    input[type="checkbox"]
      display: inline-block
      width: auto

    .button
      margin: 4rem auto 0

  .cancel
    float: left
    margin: 1rem 0 0 1rem

  label.error
    color: red

  sup
    vertical-align: super

  .error-message-bubble
    background: $action-alert
    color: $title-color
    font-size: 1.15rem
    left: 0
    margin: 0
    position: absolute
    right: 0
    text-align: center
    top: 0
    transition: all 0.7s ease
    &.slide-up
      opacity: 0
      padding: 0
    &.slide-down
      opacity: 1
      padding: 1rem

  .error-message
    margin: 5px 0
    text-align: center
</style>